/**
 * Character Memory Lite — 资产 → OperationalSnapshot 组装层（非 LLM）
 *
 * 决策约束已迁至 CharacterMemory（characterMemory.ts）。
 * 本文件只负责 buildOperationalSnapshot，供 Pipeline 聚合近期资产。
 */
import type { DecisionTone, OperationalDayRecord, OperationalSnapshot } from './types';

function isSelfieSubtype(subtype: string): boolean {
  return subtype === 'selfie' || subtype === 'mirror_selfie';
}

function isCommercialTone(tone: DecisionTone): boolean {
  return tone === 'commercial';
}

/** 从资产记录聚合单日营运快照 */
export function buildDayRecord(
  date: string,
  assets: Array<{ created_at: string; material_type: string | null; photo_subtype: string | null; asset_type: string }>,
): OperationalDayRecord {
  const dayAssets = assets.filter((a) => a.created_at.startsWith(date));
  const photoSubtypes = dayAssets
    .filter((a) => a.material_type === 'photo' || a.asset_type === 'image')
    .map((a) => a.photo_subtype || 'life')
    .filter(Boolean) as string[];

  const selfieCount = photoSubtypes.filter(isSelfieSubtype).length;
  const hadTalkingVideo = dayAssets.some((a) => a.material_type === 'talking_video');
  const hadShortVideo = dayAssets.some((a) => a.material_type === 'silent_video');

  let tone: DecisionTone = 'life';
  if (hadTalkingVideo) tone = 'commercial';
  else if (selfieCount > 0) tone = 'outreach';

  return { date, photoSubtypes, hadTalkingVideo, hadShortVideo, tone, selfieCount };
}

/** 组装完整营运记忆快照 */
export function buildOperationalSnapshot(
  characterId: number,
  asOfDate: string,
  recentDates: string[],
  assets: Array<{ created_at: string; material_type: string | null; photo_subtype: string | null; asset_type: string }>,
): OperationalSnapshot {
  const recentDays = recentDates.map((d) => buildDayRecord(d, assets));

  const weekSelfieCount = recentDays.reduce((sum, d) => sum + d.selfieCount, 0);

  let consecutiveCommercialDays = 0;
  for (let i = recentDays.length - 1; i >= 0; i--) {
    if (isCommercialTone(recentDays[i].tone)) consecutiveCommercialDays++;
    else break;
  }

  const yesterday = recentDays[recentDays.length - 1];
  const yesterdayHadSelfie = (yesterday?.selfieCount ?? 0) > 0;
  const yesterdayHadTalking = yesterday?.hadTalkingVideo ?? false;

  return {
    characterId,
    asOfDate,
    recentDays,
    weekSelfieCount,
    consecutiveCommercialDays,
    yesterdayHadSelfie,
    yesterdayHadTalking,
  };
}

/** 上海时区今日 YYYY-MM-DD */
export function shanghaiDateKey(d = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Shanghai' }).format(d);
}

/** 往前 N 天的日期键（不含今天） */
export function priorDates(count: number, fromDate = shanghaiDateKey()): string[] {
  const base = new Date(`${fromDate}T12:00:00+08:00`);
  const out: string[] = [];
  for (let i = count; i >= 1; i--) {
    const d = new Date(base);
    d.setDate(d.getDate() - i);
    out.push(shanghaiDateKey(d));
  }
  return out;
}
